<template>
  <main class="tracking-results-page">
    <div class="container">
      <router-link to="/ngo" class="back-link">
        <i class="fa fa-arrow-left mr-2" aria-hidden="true"></i>
        Back to search
      </router-link>

      <header class="results-header">
        <p class="eyebrow">Organization Lookup</p>
        <h1>Search results</h1>
        <p v-if="trackingId" class="mb-0">
          Results for tracking ID <strong>{{ trackingId }}</strong>
        </p>
      </header>

      <section v-if="loading" class="organization-card state-card" role="status" aria-live="polite">
        <p class="mb-0">Searching for your organization…</p>
      </section>

      <section v-else-if="hasDuplicateTrackingIdError" class="tracking-conflict-card">
        <div class="tracking-conflict-card__icon" aria-hidden="true">
          <i class="fa fa-files-o"></i>
        </div>
        <p class="eyebrow">Record needs review</p>
        <h2>This tracking ID is shared by multiple organizations</h2>
        <p class="tracking-conflict-card__message">{{ error }}</p>
        <div class="tracking-conflict-card__id">
          <span>Tracking ID</span>
          <strong>{{ trackingId }}</strong>
        </div>
        <form class="result-search-form" @submit.prevent="searchForTrackingId">
          <label class="sr-only" for="conflict-tracking-id">Search with another tracking ID</label>
          <input
            id="conflict-tracking-id"
            v-model="searchTrackingId"
            type="search"
            placeholder="Enter another tracking ID"
            autocomplete="off"
          />
          <button class="btn btn-success" type="submit" :disabled="!searchTrackingId.trim()">
            Search again
          </button>
        </form>
      </section>

      <section v-else-if="error" class="organization-card state-card">
        <h2>Unable to load results</h2>
        <p>{{ error }}</p>
        <form class="result-search-form" @submit.prevent="searchForTrackingId">
          <label class="sr-only" for="error-tracking-id">Search with another tracking ID</label>
          <input
            id="error-tracking-id"
            v-model="searchTrackingId"
            type="search"
            placeholder="Enter another tracking ID"
            autocomplete="off"
          />
          <button class="btn btn-success" type="submit" :disabled="!searchTrackingId.trim()">
            Search again
          </button>
        </form>
      </section>

      <div v-else-if="organization" class="profile-layout">
        <article class="profile-hero">
          <div class="profile-heading">
            <div class="organization-monogram" aria-hidden="true">{{ initials }}</div>
            <div class="profile-title">
              <p class="eyebrow">Organization profile</p>
              <h2>{{ organization.name }}</h2>
              <div class="profile-meta">
                <span
                  ><i class="fa fa-map-marker" aria-hidden="true"></i>
                  {{ organization.state || 'Location not provided' }}</span
                >
                <span v-if="organization.establishment_year"
                  >Established {{ organization.establishment_year }}</span
                >
              </div>
            </div>
            <span class="status-badge" :class="registrationTone">{{
              organization.status || 'Status unavailable'
            }}</span>
          </div>
          <div class="hero-facts">
            <div>
              <span class="fact-label">Tracking ID</span
              ><strong class="tracking-code">{{ organization.tracking_id || trackingId }}</strong>
            </div>
            <div>
              <span class="fact-label">File No.</span
              ><strong class="tracking-code">{{ organization.file_no || 'Not provided' }}</strong>
            </div>
            <div>
              <span class="fact-label">Organization type</span
              ><strong>{{ organization.type || 'Not provided' }}</strong>
            </div>
            <div>
              <span class="fact-label">Geographic zone</span
              ><strong>{{ organization.geo_zone || 'Not provided' }}</strong>
            </div>
          </div>
        </article>

        <div class="profile-tabs" role="tablist" aria-label="Organization management">
          <button
            v-for="(tab, index) in tabs"
            :id="`tab-${tab.id}`"
            :key="tab.id"
            type="button"
            role="tab"
            :aria-selected="activeTab === tab.id"
            :aria-controls="`panel-${tab.id}`"
            :tabindex="activeTab === tab.id ? 0 : -1"
            @click="activeTab = tab.id"
            @keydown="navigateTabs($event, index)"
          >
            {{ tab.label }}
          </button>
        </div>
        <div
          id="panel-overview"
          v-show="activeTab === 'overview'"
          class="overview-panel"
          role="tabpanel"
          aria-labelledby="tab-overview"
          tabindex="0"
        >
          <div class="profile-main">
            <section class="detail-card">
              <div class="card-heading">
                <span class="section-icon"
                  ><i class="fa fa-building-o" aria-hidden="true"></i
                ></span>
                <div>
                  <p class="eyebrow">Purpose &amp; impact</p>
                  <h3>Organization overview</h3>
                </div>
              </div>
              <p class="overview-copy">
                {{
                  organization.organization_focus ||
                  organization.description ||
                  'An organization overview has not been provided.'
                }}
              </p>
              <h4 class="detail-label">Program areas</h4>
              <div class="program-tags">
                <span v-for="area in programAreaNames" :key="area">{{ area }}</span>
                <p v-if="!programAreaNames.length" class="muted mb-0">Not provided</p>
              </div>
            </section>

            <section class="detail-card">
              <div class="card-heading">
                <span class="section-icon"
                  ><i class="fa fa-map-marker" aria-hidden="true"></i
                ></span>
                <div>
                  <p class="eyebrow">Get in touch</p>
                  <h3>Location &amp; contact</h3>
                </div>
              </div>
              <dl class="contact-grid">
                <div class="full-width">
                  <dt>Office address</dt>
                  <dd>{{ organization.address || 'Not provided' }}</dd>
                </div>
                <div>
                  <dt>Email address</dt>
                  <dd>
                    <a v-if="organization.email" :href="`mailto:${organization.email}`">{{
                      organization.email
                    }}</a
                    ><span v-else>Not provided</span>
                  </dd>
                </div>
                <div>
                  <dt>Telephone</dt>
                  <dd>{{ organization.phone_number || 'Not provided' }}</dd>
                </div>
                <div v-if="websiteUrl" class="full-width">
                  <dt>Website</dt>
                  <dd>
                    <a :href="websiteUrl" target="_blank" rel="noopener noreferrer"
                      >{{ organization.web_url }}
                      <i class="fa fa-external-link" aria-hidden="true"></i
                    ></a>
                  </dd>
                </div>
              </dl>
            </section>

            <section v-if="contacts.length" class="detail-card">
              <div class="card-heading">
                <span class="section-icon"><i class="fa fa-user-o" aria-hidden="true"></i></span>
                <div>
                  <p class="eyebrow">People</p>
                  <h3>Principal contacts</h3>
                </div>
              </div>
              <div
                v-for="(contact, index) in contacts"
                :key="contact.id || index"
                class="contact-person"
              >
                <div class="contact-avatar" aria-hidden="true"><i class="fa fa-user"></i></div>
                <div>
                  <h4>{{ contact.full_name || 'Contact person' }}</h4>
                  <p v-if="contact.position" class="muted">{{ contact.position }}</p>
                  <div class="contact-person-details">
                    <a v-if="contact.email" :href="`mailto:${contact.email}`">{{ contact.email }}</a
                    ><span v-if="contact.phone_number">{{ contact.phone_number }}</span>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <aside class="profile-sidebar" aria-label="Registration details">
            <section class="detail-card registration-card">
              <div class="card-heading">
                <span class="section-icon"
                  ><i class="fa fa-file-text-o" aria-hidden="true"></i
                ></span>
                <div>
                  <p class="eyebrow">Official record</p>
                  <h3>Registration</h3>
                </div>
              </div>
              <dl class="record-list">
                <div>
                  <dt>Registration status</dt>
                  <dd>
                    <span class="status-badge" :class="registrationTone">{{
                      organization.status || 'Not provided'
                    }}</span>
                  </dd>
                </div>
                <div>
                  <dt>Registered on</dt>
                  <dd>{{ formatDate(organization.registered_on) }}</dd>
                </div>
              </dl>
            </section>
            <p class="record-updated">Last updated {{ formatDate(organization.updated_on) }}</p>
          </aside>
        </div>
        <section
          id="panel-subscriptions"
          v-show="activeTab === 'subscriptions'"
          class="management-panel"
          role="tabpanel"
          aria-labelledby="tab-subscriptions"
          tabindex="0"
        >
          <section class="detail-card subscription-card">
            <p class="eyebrow">Membership</p>
            <h3>Registration</h3>
            <span class="status-badge subscription-status" :class="subscriptionTone">{{
              subscriptionLabel
            }}</span>
            <p v-if="subscription.is_expired" class="subscription-note">
              The registration on this record has expired.
            </p>
            <dl class="record-list">
              <div>
                <dt>Expiry date</dt>
                <dd>{{ formatDate(subscription.expires_on) }}</dd>
              </div>
              <div>
                <dt>Last renewed</dt>
                <dd>{{ formatDate(organization.renewed_on || subscription.renewed_on) }}</dd>
              </div>
            </dl>
            <!-- <button
              type="button"
              class="btn btn-success mt-4"
              :disabled="!organization.id || renewingSubscription"
              @click="showRenewModal = true"
            >
              Renew registration
            </button>
            <p v-if="!organization.id" class="subscription-note">
              Renewal is unavailable because this record has no organization ID.
            </p> -->
          </section>
          <section class="detail-card renewal-history" aria-labelledby="renewal-history-title">
            <div class="renewal-history-heading">
              <div>
                <p class="eyebrow">Membership record</p>
                <h3 id="renewal-history-title">Renewal history</h3>
              </div>
            </div>
            <p v-if="!renewalYears.length" class="muted">No renewal history available.</p>
            <ol v-else class="renewal-years" aria-label="Renewal years, newest first">
              <li v-for="year in renewalYears" :key="year">
                <span class="renewal-year-icon" aria-hidden="true"
                  ><i class="fa fa-calendar"></i
                ></span>
                <div>
                  <span class="detail-label">Renewal year</span>
                  <strong>{{ year }}</strong>
                </div>
              </li>
            </ol>
          </section>
        </section>
        <section
          id="panel-reports"
          v-show="activeTab === 'reports'"
          class="management-panel detail-card"
          role="tabpanel"
          aria-labelledby="tab-reports"
          tabindex="0"
        >
          <organization-annual-reports
            v-if="activeTab === 'reports'"
            :key="requestId"
            :tracking-id="trackingId"
            :annual-reports="organization.annual_reports"
            @reports-loaded="$set(organization, 'annual_reports', $event)"
          />
        </section>
        <renew-subscription
          :visible="showRenewModal"
          :subscription="{ organizationName: organization.name, endDate: subscription.expires_on }"
          :loading="renewingSubscription"
          @close="closeRenewModal"
          @confirm="renewSubscription"
        />
      </div>

      <section v-else-if="trackingId" class="organization-card state-card" role="status">
        <h2>No organization found</h2>
        <p class="mb-0">Check the tracking ID and return to search to try again.</p>
      </section>

      <section v-else class="organization-card state-card">
        <h2>Enter a tracking ID to get started</h2>
        <p class="mb-0">Return to the search section and enter an organization's tracking ID.</p>
      </section>
    </div>
  </main>
</template>

<script>
import { getOrganizationByTrackingId } from '../../config/api-modules/api.organization';
import OrganizationAnnualReports from './OrganizationAnnualReports.vue';
import RenewSubscription from '../../../admin/views/modal/RenewSubscription.vue';
import { renewOrganizationSubscription } from '../../../admin/config/api-modules/api.subscriptions';

export default {
  name: 'NgoTrackingResults',
  components: { OrganizationAnnualReports, RenewSubscription },
  data() {
    return {
      organization: null,
      loading: false,
      error: '',
      errorCode: '',
      searchTrackingId: '',
      requestId: 0,
      activeTab: 'overview',
      showRenewModal: false,
      renewingSubscription: false,
      tabs: [
        { id: 'overview', label: 'Overview' },
        { id: 'subscriptions', label: 'Registrations' },
        { id: 'reports', label: 'Annual Reports' },
      ],
    };
  },
  computed: {
    renewalYears() {
      const years = this.organization?.years_renewed;
      if (!Array.isArray(years)) return [];
      const validYears = years
        .filter((year) => typeof year === 'number' || (typeof year === 'string' && year.trim()))
        .map(Number)
        .filter((year) => Number.isInteger(year) && year > 0);
      return [...new Set(validYears)].sort((a, b) => b - a);
    },
    trackingId() {
      const value = this.$route.query.tracking_id;
      return typeof value === 'string' ? value.trim() : '';
    },
    programAreaNames() {
      const areas = this.organization?.areas_of_focus;
      if (typeof areas === 'string') return areas ? [areas] : [];
      if (!Array.isArray(areas)) return [];
      return [
        ...new Set(
          areas.map((area) => (typeof area === 'string' ? area : area?.name)).filter(Boolean)
        ),
      ];
    },
    initials() {
      return (this.organization?.name || '')
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase();
    },
    contacts() {
      return Array.isArray(this.organization?.contact_persons)
        ? this.organization.contact_persons.filter(Boolean)
        : [];
    },
    subscription() {
      return this.organization?.subscription || {};
    },
    subscriptionLabel() {
      if (this.subscription.is_expired) return 'Expired';
      return (
        this.organization?.subscription_status ||
        this.subscription.subscription_status ||
        'Not provided'
      );
    },
    subscriptionTone() {
      if (this.subscription.is_expired) return 'tone-warning';
      return this.subscriptionLabel.toLowerCase() === 'active' ? 'tone-success' : 'tone-neutral';
    },
    registrationTone() {
      const status = this.organization?.status?.toLowerCase();
      if (status === 'approved') return 'tone-success';
      if (status === 'rejected' || status === 'suspended') return 'tone-danger';
      return 'tone-neutral';
    },
    websiteUrl() {
      const value = this.organization?.web_url;
      if (!value) return '';
      try {
        const url = new URL(value);
        return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
      } catch {
        return '';
      }
    },
    hasDuplicateTrackingIdError() {
      return this.errorCode === 'duplicate_tracking_id';
    },
  },
  watch: {
    trackingId: {
      immediate: true,
      handler: 'loadOrganization',
    },
  },
  beforeDestroy() {
    this.requestId += 1;
  },
  methods: {
    closeRenewModal() {
      if (!this.renewingSubscription) this.showRenewModal = false;
    },
    async renewSubscription(payload) {
      if (this.renewingSubscription || !this.organization?.id || !payload?.year) return;
      const { requestId, trackingId } = this;
      this.renewingSubscription = true;
      try {
        await renewOrganizationSubscription(this.organization.id, { year: payload.year });
        if (requestId !== this.requestId) return;
        this.showRenewModal = false;
        this.$toast.show({ type: 'SUCCESS', message: `Registration renewed for ${payload.year}.` });
        try {
          const { data } = await getOrganizationByTrackingId(trackingId);
          if (requestId !== this.requestId) return;
          const organization =
            data && Object.prototype.hasOwnProperty.call(data, 'data') ? data.data : data;
          if (!organization?.name || Array.isArray(organization)) {
            throw new Error('Unexpected organization response');
          }
          this.organization = organization;
        } catch (error) {
          if (requestId === this.requestId) {
            this.$toast.show({
              type: 'ERROR',
              message:
                'The registration was renewed, but its details could not be refreshed. Reload the page to see the latest status.',
            });
          }
        }
      } catch (error) {
        if (requestId === this.requestId) {
          this.$toast.show({
            type: 'ERROR',
            message:
              error.response?.data?.message ||
              error.response?.data?.detail ||
              'Unable to renew the registration. Please try again.',
          });
        }
      } finally {
        if (requestId === this.requestId) this.renewingSubscription = false;
      }
    },
    navigateTabs(event, index) {
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let next =
        (index + (event.key === 'ArrowLeft' ? -1 : 1) + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      this.activeTab = this.tabs[next].id;
      this.$nextTick(() => this.$el.querySelector(`#tab-${this.activeTab}`).focus());
    },
    formatDate(value) {
      if (!value) return 'Not provided';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Not provided';
      return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(date);
    },
    async loadOrganization() {
      this.requestId += 1;
      const { requestId } = this;
      this.organization = null;
      this.activeTab = 'overview';
      this.showRenewModal = false;
      this.renewingSubscription = false;
      this.error = '';
      this.errorCode = '';
      this.searchTrackingId = this.trackingId;
      this.loading = false;
      if (!this.trackingId) return;

      this.loading = true;
      try {
        const { data } = await getOrganizationByTrackingId(this.trackingId);
        if (requestId !== this.requestId) return;
        const organization =
          data && Object.prototype.hasOwnProperty.call(data, 'data') ? data.data : data;
        if (
          organization !== null &&
          organization !== undefined &&
          (typeof organization !== 'object' || Array.isArray(organization) || !organization.name)
        ) {
          throw new Error('Unexpected organization response');
        }
        this.organization = organization || null;
      } catch (error) {
        if (requestId !== this.requestId) return;
        if (error.response?.status !== 404) {
          this.errorCode = error.response?.data?.code || '';
          this.error =
            error.response?.data?.message ||
            error.response?.data?.detail ||
            'We could not retrieve the organization. Please try again.';
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    searchForTrackingId() {
      const trackingId = this.searchTrackingId.trim();
      if (!trackingId) return;

      if (trackingId === this.trackingId) {
        this.loadOrganization();
        return;
      }

      this.$router.push({
        name: 'ngo tracking results',
        query: { tracking_id: trackingId },
      });
    },
  },
};
</script>

<style scoped>
.tracking-results-page {
  min-height: 75vh;
  padding: 2.5rem 0 4rem;
  background: radial-gradient(ellipse at top right, #e4efe6, transparent 55%), #f5f7f4;
  color: #20382b;
  overflow-wrap: anywhere;
}
.back-link {
  display: inline-flex;
  align-items: center;
  margin-bottom: 1.75rem;
  color: #365f46;
  font-weight: 600;
  font-size: 0.9rem;
}
.results-header {
  padding-bottom: 2rem;
}
.results-header h1 {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  letter-spacing: -0.045em;
  margin-bottom: 0.6rem;
}
.results-header > p:last-child {
  color: #627468;
}
.eyebrow {
  margin-bottom: 0.45rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #637d6b;
}
.profile-layout,
.overview-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 1.5rem;
  align-items: start;
}
.overview-panel,
.profile-tabs,
.management-panel {
  grid-column: 1 / -1;
  min-width: 0;
}
.profile-tabs {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.5rem;
  border-radius: 16px;
  background: #e7eee4;
}
.profile-tabs button {
  border: 0;
  border-radius: 10px;
  padding: 0.85rem 1.25rem;
  background: transparent;
  color: #365f46;
  font-weight: 600;
  white-space: nowrap;
}
.profile-tabs button[aria-selected='true'] {
  background: #133323;
  color: #fff;
}
.profile-hero {
  grid-column: 1 / -1;
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 2.5rem;
  background: radial-gradient(ellipse at top right, #345d45, transparent 65%), #133323;
  color: #fff;
  box-shadow: 0 18px 42px #17362418;
}
.profile-heading {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}
.organization-monogram {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 76px;
  height: 76px;
  border: 1px solid #ffffff30;
  border-radius: 22px;
  background: #ffffff12;
  font-size: 1.7rem;
  color: #dfebbc;
  letter-spacing: -0.04em;
}
.profile-title {
  flex: 1;
  min-width: 0;
}
.profile-title .eyebrow {
  color: #b6caae;
}
.profile-title h2 {
  font-size: clamp(1.5rem, 3vw, 2rem);
  line-height: 1.25;
  font-weight: 600;
  letter-spacing: -0.025em;
  margin-bottom: 0.85rem;
}
.profile-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  color: #ccdace;
  font-size: 0.85rem;
}
.profile-meta i {
  margin-right: 0.4rem;
}
.hero-facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #ffffff24;
}
.fact-label {
  display: block;
  color: #b6caae;
  font-size: 0.7rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.hero-facts strong {
  font-size: 0.92rem;
  font-weight: 500;
}
.tracking-code {
  font-family: monospace;
  letter-spacing: 0.04em;
}
.profile-main,
.profile-sidebar {
  display: grid;
  gap: 1.5rem;
  min-width: 0;
}
.detail-card,
.organization-card {
  padding: 1.8rem;
  border: 1px solid #e1e8df;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 6px 24px #18342204;
}
.card-heading {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 1.5rem;
}
.section-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 12px;
  background: #f0f5ee;
  color: #54734a;
}
.detail-card h3 {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 700;
  letter-spacing: -0.02em;
}
.overview-copy {
  font-size: 0.98rem;
  line-height: 1.85;
  color: #53665a;
  margin-bottom: 1.75rem;
}
.detail-label,
dt {
  color: #6a796f;
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}
.program-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.program-tags > span {
  padding: 0.5rem 0.85rem;
  border-radius: 8px;
  border: 1px solid #dfebda;
  color: #3e6532;
  background: #f4f8f0;
  font-size: 0.82rem;
  font-weight: 600;
}
.contact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
  margin: 0;
}
.full-width {
  grid-column: 1 / -1;
}
.contact-grid dd {
  margin: 0;
  line-height: 1.7;
  font-size: 0.9rem;
}
.detail-card a {
  color: #2f7149;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.contact-person {
  display: flex;
  gap: 1rem;
  padding: 1.25rem 0;
  border-top: 1px solid #edf0eb;
}
.contact-person:last-child {
  padding-bottom: 0;
}
.contact-person h4 {
  font-size: 0.95rem;
  margin-bottom: 0.4rem;
  font-weight: 600;
}
.contact-avatar {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f2f4ef;
  color: #7c8d72;
}
.contact-person-details {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1.25rem;
  font-size: 0.83rem;
  color: #627468;
}
.muted {
  color: #738176;
  font-size: 0.85rem;
}
.record-list {
  margin: 0;
}
.record-list > div {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.95rem 0;
  border-bottom: 1px solid #edf0eb;
}
.record-list > div:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}
.record-list dt {
  margin: 0;
}
.record-list dd {
  margin: 0;
  text-align: right;
  font-size: 0.8rem;
  font-weight: 600;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.7rem;
  border-radius: 7px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
}
.tone-success {
  background: #e7f3e9;
  color: #276239;
}
.tone-warning {
  background: #fff0d4;
  color: #87581a;
}
.tone-danger {
  background: #fae8e6;
  color: #a63d35;
}
.tone-neutral {
  background: #eef0ef;
  color: #57675e;
}
.subscription-card {
  background: #fbfcf9;
}
.renewal-history {
  margin-top: 1.5rem;
}
.renewal-history-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}
.renewal-years {
  list-style: none;
  padding: 0;
  margin: 1.5rem 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.85rem;
}
.renewal-years li {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem;
  border: 1px solid #e1e8df;
  border-radius: 12px;
  background: #fbfcf9;
}
.renewal-year-icon {
  color: #54734a;
}
.renewal-years .detail-label,
.renewal-years strong {
  display: block;
}
.renewal-years strong {
  font-size: 1.25rem;
}
.subscription-status {
  margin-top: 1rem;
}
.subscription-note {
  font-size: 0.8rem;
  line-height: 1.6;
  margin: 0.8rem 0 0;
  color: #7d6c4e;
}
.record-updated {
  margin: -0.25rem 0 0;
  text-align: center;
  font-size: 0.73rem;
  color: #748276;
}
.record-updated i {
  margin-right: 0.3rem;
}
.state-card {
  text-align: center;
  padding: 3rem 1.5rem;
}
.state-card h2 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
}
.tracking-conflict-card {
  position: relative;
  overflow: hidden;
  max-width: 720px;
  margin: 0 auto;
  padding: 3rem;
  border: 1px solid #e2d9bc;
  border-radius: 28px;
  background: radial-gradient(circle at top right, #fff8df, transparent 42%), #fffdf7;
  box-shadow: 0 18px 42px #5f4a1312;
  text-align: center;
}
.tracking-conflict-card__icon {
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  margin: 0 auto 1.25rem;
  border-radius: 18px;
  background: #f5eccf;
  color: #80651a;
  font-size: 1.45rem;
}
.tracking-conflict-card h2 {
  max-width: 540px;
  margin: 0 auto 1rem;
  color: #34311d;
  font-size: clamp(1.5rem, 3vw, 2rem);
  letter-spacing: -0.035em;
}
.tracking-conflict-card__message {
  max-width: 590px;
  margin: 0 auto 1.5rem;
  color: #665f49;
  line-height: 1.7;
}
.tracking-conflict-card__id {
  display: inline-flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 220px;
  margin-bottom: 1.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #e5d9b4;
  border-radius: 12px;
  background: #ffffffa8;
}
.tracking-conflict-card__id span {
  color: #8a7a47;
  font-size: 0.67rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.tracking-conflict-card__id strong {
  color: #4d4429;
  font-family: monospace;
  font-size: 0.92rem;
}
.result-search-form {
  display: flex;
  justify-content: center;
  gap: 0.65rem;
  max-width: 520px;
  margin: 0 auto;
}
.result-search-form input {
  min-width: 0;
  flex: 1;
  padding: 0.7rem 0.9rem;
  border: 1px solid #ccd8cd;
  border-radius: 10px;
  background: #ffffff;
  color: #20382b;
}
.result-search-form input:focus {
  border-color: #638f59;
  outline: none;
  box-shadow: 0 0 0 3px #96b9753b;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
a:focus-visible,
button:focus-visible {
  outline: 3px solid #96b975;
  outline-offset: 4px;
}
@media (max-width: 991px) {
  .tracking-results-page {
    padding-top: 5rem;
  }
  .profile-layout,
  .overview-panel {
    grid-template-columns: minmax(0, 1fr);
  }
  .profile-sidebar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .record-updated {
    grid-column: 1 / -1;
  }
}
@media (max-width: 575px) {
  .profile-hero,
  .detail-card,
  .tracking-conflict-card {
    padding: 1.4rem;
    border-radius: 18px;
  }
  .profile-heading {
    flex-wrap: wrap;
    gap: 1rem;
  }
  .organization-monogram {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    font-size: 1.2rem;
  }
  .profile-title {
    flex-basis: calc(100% - 70px);
  }
  .hero-facts,
  .contact-grid,
  .profile-sidebar {
    grid-template-columns: minmax(0, 1fr);
  }
  .hero-facts {
    gap: 1.25rem;
  }
  .profile-layout,
  .profile-main,
  .profile-sidebar {
    gap: 1rem;
  }
  .result-search-form {
    flex-direction: column;
  }
  .result-search-form .btn {
    width: 100%;
  }
}
</style>
